/*
Badge catalogue — mirrors Android Badge.kt after v1.2.0 *Progression*.

Catalogue total (post-v1.2.0) :
  - 10 binaires statiques  (alignés Android v1.2.0 BadgeCatalog.ALL)
  - 26 binaires dynamiques « Familier des [genre] »  (1 par genre éligible ≥ 7 espèces)
  - 22 binaires dynamiques « Familier du [arr] »      (20 arrondissements + 2 bois)
  - 3 progressifs PWA Marcheur / Botaniste / Chasseur (17 paliers cumulés, hérités du dev d'origine)
  ─────────────────────────────────────────────────────────────────────
  = 75 paliers cumulés au total.

Le mirror v1.2.0 a retiré strictement 3 binaires hérités, absorbés par
les familles « Familier » et les raretés exactes 1..5 :
  - `espece_rare` (< 100 ind.) → absorbé par les raretés exactes 1..5
  - `tourneur_de_paris` (10 arr.) → absorbé par « Familier du [arr] »
  - `tour_complet` (20 arr.) → absorbé par « Familier du [arr] »
Les utilisateurs existants verront ces 3 badges disparaître du catalogue
après sync — décision actée 2026-05-14, parité stricte vs Android.

Les badges dynamiques sont générés au runtime depuis les datasets après
`await ensureSpeciesIndex() + ensureGenreInfo() + ensureArrSpecies()`.
Le catalogue complet est exposé par `fullCatalog()` (mémoïsé).

Ajouter un badge statique : entrée dans `kStaticCatalog` + critère dans
`badgeEvaluator.ts` + (option) glyph dans `kBadgeIdGlyphs`. Pour un badge
dynamique, étendre `genreBadges()` / `arrBadges()` selon la famille.
*/

import { genreInfoFor } from './genreInfo'
import { allGenres, genreCount } from './speciesIndex'
import { arrSpeciesKeys } from './arrSpecies'
import {
	arrKeyIdSlug,
	arrKeyLabel,
	type ArrKey,
} from './arrKey'

export type BadgeCategory =
	| 'decouverte'
	| 'botanique'
	| 'geographie'
	| 'remarquables'
	| 'demesure'

export const kBadgeCategoryLabels: Record<BadgeCategory, string> = {
	decouverte: 'Découverte',
	botanique: 'Botanique',
	geographie: 'Géographie',
	remarquables: 'Remarquables',
	demesure: 'Démesure',
}

export type BadgeKind = 'binary' | 'progressive'
export type ProgressiveUnit = 'captures' | 'espèces' | 'remarquables'

export interface ProgressiveTier
{
	threshold: number
	label: string
}

interface BadgeDefCommon
{
	id: string
	label: string
	description: string
	category: BadgeCategory
	implemented: boolean  // true when the PWA dataset carries enough info to evaluate this badge
}

export interface BinaryBadgeDef extends BadgeDefCommon
{
	kind: 'binary'
}

export interface ProgressiveBadgeDef extends BadgeDefCommon
{
	kind: 'progressive'
	unit: ProgressiveUnit
	tiers: readonly ProgressiveTier[]
}

export type BadgeDef = BinaryBadgeDef | ProgressiveBadgeDef

export interface BinaryBadgeState
{
	kind: 'binary'
	def: BinaryBadgeDef
	unlockedAt: number | null  // epoch ms of the capture that flipped the criterion, null when still locked
}

export interface ProgressiveBadgeState
{
	kind: 'progressive'
	def: ProgressiveBadgeDef
	currentCount: number  // running progress at the end of the captures stream — captures / species / remarquables count
	tierUnlocks: (number | null)[]  // same length as def.tiers, null when the tier hasn't been reached yet
}

export type BadgeState = BinaryBadgeState | ProgressiveBadgeState

/*
Constantes pour les familles dynamiques. `genreBadgeId` / `arrBadgeId`
restent stables tag-à-tag (même slug = même id, même si le libellé
français évolue).
*/
export const GENRE_FAMILIER_MIN_SPECIES = 7  // un genre obtient un badge ssi ≥ 7 espèces identifiées
export const FAMILIER_GENRE_PREFIX = 'familier_genre_'
export const FAMILIER_ARR_PREFIX = 'familier_arr_'
export const POKEDEX_PREFIX = 'pokedex_'

export function genreBadgeId(inGenre: string): string
{
	return FAMILIER_GENRE_PREFIX + inGenre.toLowerCase().replace(/ /g, '_')
}

export function arrBadgeId(inKey: ArrKey): string
{
	return FAMILIER_ARR_PREFIX + arrKeyIdSlug(inKey)
}

/*
6 paliers Pokédex (v1.4.0 sprint S5). Critère : capturer toutes les
espèces actives ayant `pokedexNumber ∈ [1..N]` hors remarquables.
Libellés gradués Promeneur curieux → Dendrologue. Mirror Android
BadgeCatalog.POKEDEX_THRESHOLDS + POKEDEX_LABELS.
*/
export const kPokedexThresholds: readonly number[] = Object.freeze([10, 20, 50, 100, 200, 500])

export const kPokedexLabels: Readonly<Record<number, { label: string; description: string }>> = Object.freeze({
	10:  { label: 'Promeneur curieux',  description: 'Les 10 espèces les plus communes de Paris, capturées.' },
	20:  { label: 'Apprenti botaniste', description: 'Les 20 espèces les plus communes de Paris, capturées.' },
	50:  { label: 'Botaniste',          description: 'Les 50 espèces les plus communes de Paris, capturées.' },
	100: { label: 'Herboriste',         description: 'Les 100 espèces les plus communes de Paris, capturées.' },
	200: { label: 'Naturaliste',        description: 'Les 200 espèces les plus communes de Paris, capturées.' },
	500: { label: 'Dendrologue',        description: 'Les 500 espèces les plus communes de Paris, capturées.' },
})

export function pokedexBadgeId(inThreshold: number): string
{
	return POKEDEX_PREFIX + inThreshold
}

/*
Tier definitions for the 3 progressive badges. Hérités du dev d'origine —
couche PWA-only par-dessus le catalogue Android (qui ne porte que des
binaires en v1.2.0).
*/
const kMarcheurTiers: readonly ProgressiveTier[] = Object.freeze([
	{ threshold: 1,   label: 'Première capture' },
	{ threshold: 10,  label: 'Promenade' },
	{ threshold: 25,  label: 'Flâneur' },
	{ threshold: 50,  label: 'Marcheur' },
	{ threshold: 100, label: 'Centurion' },
	{ threshold: 250, label: 'Endurance' },
]) as readonly ProgressiveTier[]

const kBotanisteTiers: readonly ProgressiveTier[] = Object.freeze([
	{ threshold: 1,   label: 'Curieux' },
	{ threshold: 10,  label: 'Apprenti' },
	{ threshold: 25,  label: 'Pousse' },
	{ threshold: 50,  label: 'Amateur' },
	{ threshold: 100, label: 'Passionné' },
	{ threshold: 200, label: 'Confirmé' },
]) as readonly ProgressiveTier[]

const kChasseurTiers: readonly ProgressiveTier[] = Object.freeze([
	{ threshold: 1,  label: 'Premier remarquable' },
	{ threshold: 5,  label: 'Connaisseur' },
	{ threshold: 10, label: 'Chasseur' },
	{ threshold: 25, label: 'Expert' },
	{ threshold: 50, label: 'Légende' },
]) as readonly ProgressiveTier[]

/*
Static catalogue (10 binaires + 3 progressifs). Order is meaningful — the
badges screen displays binary badges grouped by `category` in this order,
with progressive badges rendered full-width within their category.
*/
export const kStaticCatalog: readonly BadgeDef[] = Object.freeze([
	// DECOUVERTE — 1 binaire + 1 progressif
	{ kind: 'binary', id: 'premiere_capture', label: 'Première capture',
	  description: 'Ton premier arbre révélé.',
	  category: 'decouverte', implemented: true },
	{ kind: 'progressive', id: 'marcheur', label: 'Marcheur',
	  description: 'Capture des arbres pour progresser.',
	  category: 'decouverte', implemented: true,
	  unit: 'captures', tiers: kMarcheurTiers },

	// BOTANIQUE — 5 raretés exactes + 1 progressif (genre dynamiques s'ajoutent ici via fullCatalog())
	{ kind: 'binary', id: 'espece_unique', label: 'Unique',
	  description: "Une espèce dont il n'existe qu'un seul arbre dans Paris.",
	  category: 'botanique', implemented: true },
	{ kind: 'binary', id: 'espece_couple', label: 'Couple',
	  description: "Une espèce dont il n'existe que deux arbres dans Paris.",
	  category: 'botanique', implemented: true },
	{ kind: 'binary', id: 'espece_trinite', label: 'Trinité',
	  description: "Une espèce dont il n'existe que trois arbres dans Paris.",
	  category: 'botanique', implemented: true },
	{ kind: 'binary', id: 'espece_quatuor', label: 'Quatuor',
	  description: "Une espèce dont il n'existe que quatre arbres dans Paris.",
	  category: 'botanique', implemented: true },
	{ kind: 'binary', id: 'espece_quintette', label: 'Quintette',
	  description: "Une espèce dont il n'existe que cinq arbres dans Paris.",
	  category: 'botanique', implemented: true },
	{ kind: 'progressive', id: 'botaniste', label: 'Botaniste',
	  description: 'Découvre des espèces différentes.',
	  category: 'botanique', implemented: true,
	  unit: 'espèces', tiers: kBotanisteTiers },

	// REMARQUABLES — 1 progressif
	{ kind: 'progressive', id: 'chasseur', label: 'Chasseur',
	  description: 'Capture des arbres remarquables.',
	  category: 'remarquables', implemented: true,
	  unit: 'remarquables', tiers: kChasseurTiers },

	// DEMESURE — 4 binaires
	{ kind: 'binary', id: 'geant', label: 'Géant',
	  description: 'Un arbre de plus de 30 m de haut.',
	  category: 'demesure', implemented: true },
	{ kind: 'binary', id: 'bonsai', label: 'Bonsaï',
	  description: 'Un arbre de moins de 2 m de haut.',
	  category: 'demesure', implemented: true },
	{ kind: 'binary', id: 'vieux_sage', label: 'Vieux sage',
	  description: 'Un arbre de plus de 4 m de circonférence.',
	  category: 'demesure', implemented: true },
	{ kind: 'binary', id: 'jeune_pousse', label: 'Jeune pousse',
	  description: 'Un arbre de moins de 10 cm de circonférence.',
	  category: 'demesure', implemented: true },
])

/*
Mapping rareté → espece_XYZ id. Aligné Android `BadgeCatalog.ESPECE_RARETE`.
Le critère est le compte exact de l'espèce dans le dataset, pas un ≤.
*/
export const kRarityBadgeId: Readonly<Record<number, string>> = Object.freeze({
	1: 'espece_unique',
	2: 'espece_couple',
	3: 'espece_trinite',
	4: 'espece_quatuor',
	5: 'espece_quintette',
})

/*
Pluriel français du nom vernaculaire d'un genre, minuscule initiale, pour
les libellés « Familier des chênes / bouleaux / érables ». Règle « -eau →
-eaux » + défaut « +s » : couvre les 26 genres à badge (seul irrégulier :
Bouleau). Port direct de `Badge.kt:frenchPluralLower`.
*/
export function frenchPluralLower(inNomFr: string): string
{
	const vPlural = inNomFr.endsWith('eau') ? inNomFr + 'x' : inNomFr + 's'
	return vPlural.charAt(0).toLowerCase() + vPlural.slice(1)
}

/*
Genres éligibles à un badge « Familier des ... » (≥ 7 espèces identifiées),
ordre count décroissant puis latin asc. Mirror `Badge.kt:familierGenres`.
Caller doit avoir `await ensureSpeciesIndex()`.
*/
export function familierGenres(): string[]
{
	return allGenres()
		.filter((g) => genreCount(g) >= GENRE_FAMILIER_MIN_SPECIES)
		.sort((a, b) => {
			const vDelta = genreCount(b) - genreCount(a)
			if (vDelta !== 0) return vDelta
			return a.localeCompare(b)
		})
}

/*
Badges dynamiques « Familier des [genre pluriel] ». Caller doit avoir
chargé `speciesIndex` + `genreInfo`.
*/
export function genreBadges(): BinaryBadgeDef[]
{
	return familierGenres().map((vGenre): BinaryBadgeDef => {
		const vEntry = genreInfoFor(vGenre)
		const vPlural = frenchPluralLower(vEntry?.fr ?? vGenre)
		return {
			kind: 'binary',
			id: genreBadgeId(vGenre),
			label: `Familier des ${vPlural}`,
			description: `Toutes les espèces de ${vPlural} recensées à Paris, capturées.`,
			category: 'botanique',
			implemented: true,
		}
	})
}

/*
Badges dynamiques « Familier du [arr] ». Caller doit avoir
`await ensureArrSpecies()`. Libellé :
  - Paris 1..20 → « Familier du 1er arrondissement »
  - Bois → « Familier du Bois de Vincennes »
*/
/*
Badges Pokédex statiques (6 entrées), catégorie Découverte. Libellés
ordonnés par seuil croissant — apparaissent dans cet ordre dans le
catalogue. Mirror Android v1.4.0 BadgeCatalog.pokedex().
*/
export function pokedexBadges(): BinaryBadgeDef[]
{
	return kPokedexThresholds.map((vThreshold): BinaryBadgeDef => {
		const vEntry = kPokedexLabels[vThreshold]!
		return {
			kind: 'binary',
			id: pokedexBadgeId(vThreshold),
			label: vEntry.label,
			description: vEntry.description,
			category: 'decouverte',
			implemented: true,
		}
	})
}

export function arrBadges(): BinaryBadgeDef[]
{
	return arrSpeciesKeys().map((vKey): BinaryBadgeDef => {
		const vLabelSuffix = vKey.kind === 'paris'
			? `${arrKeyLabel(vKey)} arrondissement`
			: arrKeyLabel(vKey)
		const vWhere = vKey.kind === 'paris' ? `le ${vLabelSuffix}` : `le ${arrKeyLabel(vKey)}`
		return {
			kind: 'binary',
			id: arrBadgeId(vKey),
			label: `Familier du ${vLabelSuffix}`,
			description: `Toutes les espèces d'arbres recensées dans ${vWhere}, capturées.`,
			category: 'geographie',
			implemented: true,
		}
	})
}

/*
Catalogue complet du runtime = statiques + Familier genre + Familier arr.
Mémoïsé sur la première construction (réinitialisable via
`resetFullCatalogCache()` pour les tests). Caller doit avoir loadé les
3 datasets.
*/
let fFullCatalog: BadgeDef[] | null = null

export function fullCatalog(): BadgeDef[]
{
	if (fFullCatalog) return fFullCatalog
	fFullCatalog = [
		...kStaticCatalog,
		...pokedexBadges(),
		...genreBadges(),
		...arrBadges(),
	]
	return fFullCatalog
}

export function resetFullCatalogCache(): void
{
	fFullCatalog = null
}

/*
Glyph catégorie. Les badges Familier genre / arr utilisent ce fallback
(pas de per-id glyph pour 48 dynamiques).
*/
export const kBadgeGlyphs: Record<BadgeCategory, string> = {
	decouverte: '🌱',
	botanique: '🌿',
	geographie: '🗺️',
	remarquables: '⭐',
	demesure: '🌳',
}

/*
Per-id glyph overrides. Used for the progressive badges so the
Marcheur card shows a walking icon, Chasseur shows a star, etc.
*/
export const kBadgeIdGlyphs: Record<string, string> = {
	marcheur: '🚶',
	botaniste: '🌿',
	chasseur: '⭐',
}

/*
Resolve the glyph to use for a given badge — id-level override first,
fall back to category-level glyph.
*/
export function glyphFor(inDef: BadgeDef): string
{
	return kBadgeIdGlyphs[inDef.id] ?? kBadgeGlyphs[inDef.category]
}

export interface BadgeUnlock
{
	def: BadgeDef
	tierIndex: number | null  // null for binary, otherwise the index into def.tiers
	label: string  // tier label for progressives, def.label for binaries
	unlockedAt: number
}

/*
Flatten all unlocks across the catalogue into a single list of
BadgeUnlock entries. Each progressive tier counts as an independent
unlock event. Used by the Profile "Derniers badges" preview to surface
the 3 most-recent unlocks regardless of whether they are binary or
incremental.
*/
export function flattenBadgeUnlocks(inStates: readonly BadgeState[]): BadgeUnlock[]
{
	const vOut: BadgeUnlock[] = []
	for (const vS of inStates)
	{
		if (vS.kind === 'binary')
		{
			if (vS.unlockedAt !== null)
				vOut.push({ def: vS.def, tierIndex: null, label: vS.def.label, unlockedAt: vS.unlockedAt })
		}
		else
		{
			for (let vI = 0; vI < vS.tierUnlocks.length; vI++)
			{
				const vTs = vS.tierUnlocks[vI]!
				if (vTs === null) continue
				const vTier = vS.def.tiers[vI]!
				vOut.push({ def: vS.def, tierIndex: vI, label: vTier.label, unlockedAt: vTs })
			}
		}
	}
	return vOut
}
